// @djsem/render-state — Visual Builder (Prompt 17R).
// Authoring layer for visual scenes: templates → layered builder scenes → chapter
// assignment. Builds ON the 10B/17 VisualSceneEngineLite contract: a builder scene is the
// base DJSEMVisualScene plus a layers[] document (intersection type — base validator
// untouched, rule 2). Lock/regenerate never touches locked layers (deterministic).

import type { DJSEMVisualScene } from "./scenes";
import { SeqCounter } from "@djsem/shared";
import { type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** The role a visual builder layer plays within a scene. */
export type DJSEMVisualLayerKind =
  | "background" | "particles" | "geometry" | "typography" | "stem_reactive" | "brand_overlay" | "camera_motion";

/** One authored, renderer-neutral layer within a {@link DJSEMVisualBuilderScene}. */
export interface DJSEMVisualLayer {
  id: string;
  kind: DJSEMVisualLayerKind;
  label: string;
  opacity: number;                 // 0..1
  locked: boolean;
  params: Record<string, unknown>; // renderer-neutral parameters only
  typographyMomentId?: string;     // for kind=typography
  stemDriver?: string;             // for kind=stem_reactive (stem type)
  assetRefs: string[];
  warnings: string[];
}

/** A base visual scene extended with an authored `layers[]` document, used by the visual builder. */
export type DJSEMVisualBuilderScene = DJSEMVisualScene & {
  layers: DJSEMVisualLayer[];
  sceneLocked: boolean;
  templateId?: string;
};

/** A builder scene's assignment to a chapter. */
export interface DJSEMVisualSceneAssignment { sceneId: string; chapterId: string; assignedAt: string; }

/** A starter template a builder scene can be created from. */
export interface DJSEMVisualSceneTemplate {
  id: string; label: string; requiredPlan?: string;
  baseScene: Omit<DJSEMVisualScene, "id">;
  defaultLayers: Array<Omit<DJSEMVisualLayer, "id" | "locked" | "warnings">>;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link VisualBuilderService.createSceneFromTemplate}. */
export interface CreateSceneFromTemplateInput { template: DJSEMVisualSceneTemplate; sceneId?: string; planLevel?: string; planRank?: Record<string, number>; }
/** Input for {@link VisualBuilderService.addLayer}. */
export interface AddVisualLayerInput { scene: DJSEMVisualBuilderScene; layer: Omit<DJSEMVisualLayer, "id" | "locked" | "warnings"> & { locked?: boolean }; }
/** Input for {@link VisualBuilderService.updateLayer}. */
export interface UpdateVisualLayerInput { scene: DJSEMVisualBuilderScene; layerId: string; patch: Partial<Omit<DJSEMVisualLayer, "id">>; }
/** Input for {@link VisualBuilderService.removeLayer}. */
export interface RemoveVisualLayerInput { scene: DJSEMVisualBuilderScene; layerId: string; }
/** Input for {@link VisualBuilderService.lockLayer}. */
export interface LockVisualLayerInput { scene: DJSEMVisualBuilderScene; layerId: string; locked: boolean; }
/** Input for {@link VisualBuilderService.lockScene}. */
export interface LockVisualSceneInput { scene: DJSEMVisualBuilderScene; locked: boolean; }
/** Input for {@link VisualBuilderService.regenerateUnlockedLayers}. */
export interface RegenerateUnlockedLayersInput { scene: DJSEMVisualBuilderScene; regenerate: (layer: DJSEMVisualLayer) => DJSEMVisualLayer; }
/** Input for {@link VisualBuilderService.assignSceneToChapter}. */
export interface AssignSceneToChapterInput { scene: DJSEMVisualBuilderScene; chapterId: string; assignedAt?: string; }
/** Input for {@link VisualBuilderService.validateSceneForExport}. */
export interface ValidateSceneForExportInput { scene: DJSEMVisualBuilderScene; availableAssetIds?: string[]; }

const clampUnit = (n: number, d = 1) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : d));
const _seq = new SeqCounter();

// ---- service ----------------------------------------------------------------

/**
 * Authoring layer for visual scenes: templates to layered builder scenes to chapter
 * assignment. Locking/regenerating never touches locked layers (deterministic).
 */
export class VisualBuilderService {
  /**
   * Instantiates a builder scene from a template, gating on the template's required plan
   * if a plan context is supplied.
   * @param input - the template, an optional scene id, and optional plan-gating context.
   * @returns the new builder scene (unlocked, with fresh layer/scene ids).
   * @throws if the caller's plan doesn't meet the template's required plan.
   */
  createSceneFromTemplate(input: CreateSceneFromTemplateInput): DJSEMVisualBuilderScene {
    // Plan gating: template.requiredPlan checked ONLY if a plan context is supplied.
    if (input.template.requiredPlan && input.planLevel && input.planRank) {
      const have = input.planRank[input.planLevel] ?? 0;
      const need = input.planRank[input.template.requiredPlan] ?? 99;
      if (have < need) throw new Error(`Template ${input.template.id} requires plan ${input.template.requiredPlan}.`);
    }
    return {
      ...(input.template.baseScene as DJSEMVisualScene),
      id: input.sceneId ?? `vbs_${_seq.next()}`,
      layers: input.template.defaultLayers.map((l) => ({ ...l, id: `layer_${_seq.next()}`, opacity: clampUnit(l.opacity), locked: false, warnings: [] })),
      sceneLocked: false,
      templateId: input.template.id,
    };
  }

  /**
   * Appends a new layer to a scene.
   * @param input - the scene and the layer to add.
   * @returns the updated scene.
   * @throws if the scene is locked.
   */
  addLayer(input: AddVisualLayerInput): DJSEMVisualBuilderScene {
    if (input.scene.sceneLocked) throw new Error("Scene is locked; unlock before editing.");
    const layer: DJSEMVisualLayer = { ...input.layer, id: `layer_${_seq.next()}`, opacity: clampUnit(input.layer.opacity), locked: !!input.layer.locked, warnings: [] };
    return { ...input.scene, layers: [...input.scene.layers, layer] };
  }

  /**
   * Patches an existing layer on a scene.
   * @param input - the scene, the layer id, and the patch.
   * @returns the updated scene.
   * @throws if the scene is locked, or the target layer is locked and the patch doesn't unlock it.
   */
  updateLayer(input: UpdateVisualLayerInput): DJSEMVisualBuilderScene {
    if (input.scene.sceneLocked) throw new Error("Scene is locked.");
    const layers = input.scene.layers.map((l) => {
      if (l.id !== input.layerId) return l;
      if (l.locked && input.patch.locked !== false) throw new Error(`Layer ${l.id} is locked.`);
      const next = { ...l, ...input.patch, id: l.id };
      next.opacity = clampUnit(next.opacity);
      return next;
    });
    return { ...input.scene, layers };
  }

  /**
   * Removes a layer from a scene.
   * @param input - the scene and the layer id to remove.
   * @returns the updated scene.
   * @throws if the layer is locked.
   */
  removeLayer(input: RemoveVisualLayerInput): DJSEMVisualBuilderScene {
    const l = input.scene.layers.find((x) => x.id === input.layerId);
    if (l?.locked) throw new Error(`Layer ${input.layerId} is locked; unlock before removing.`);
    return { ...input.scene, layers: input.scene.layers.filter((x) => x.id !== input.layerId) };
  }

  /**
   * Locks or unlocks a single layer.
   * @param input - the scene, layer id, and desired locked state.
   * @returns the updated scene.
   */
  lockLayer(input: LockVisualLayerInput): DJSEMVisualBuilderScene {
    return { ...input.scene, layers: input.scene.layers.map((l) => (l.id === input.layerId ? { ...l, locked: input.locked } : l)) };
  }

  /**
   * Locks or unlocks the whole scene.
   * @param input - the scene and desired locked state.
   * @returns the updated scene.
   */
  lockScene(input: LockVisualSceneInput): DJSEMVisualBuilderScene { return { ...input.scene, sceneLocked: input.locked }; }

  /**
   * Regenerates every unlocked layer via the supplied callback, leaving locked layers untouched.
   * @param input - the scene and a regenerate callback applied to each unlocked layer.
   * @returns the updated scene.
   */
  regenerateUnlockedLayers(input: RegenerateUnlockedLayersInput): DJSEMVisualBuilderScene {
    return {
      ...input.scene,
      layers: input.scene.layers.map((l) => (l.locked ? l : { ...input.regenerate(l), id: l.id, locked: false })),
    };
  }

  /**
   * Records a builder scene's assignment to a chapter.
   * @param input - the scene, target chapter id, and optional assignment timestamp.
   * @returns the assignment record.
   */
  assignSceneToChapter(input: AssignSceneToChapterInput): DJSEMVisualSceneAssignment {
    return { sceneId: input.scene.id, chapterId: input.chapterId, assignedAt: input.assignedAt ?? "1970-01-01T00:00:00.000Z" };
  }

  /**
   * Validates a builder scene is ready for export: valid layer opacities, required
   * typography/stem references present, referenced assets available, and no
   * renderer/hardware-looking param keys.
   * @param input - the scene and optionally the ids of assets currently available.
   * @returns a validation result listing any issues.
   */
  validateSceneForExport(input: ValidateSceneForExportInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    const s = input.scene;
    if (!s?.id) issues.push({ path: "$.id", message: "id required" });
    for (const l of s?.layers ?? []) {
      if (!(l.opacity >= 0 && l.opacity <= 1)) issues.push({ path: `$.layers.${l.id}.opacity`, message: "opacity must be 0..1" });
      if (l.kind === "typography" && !l.typographyMomentId) issues.push({ path: `$.layers.${l.id}`, message: "typography layer must reference a typography moment" });
      if (l.kind === "stem_reactive" && !l.stemDriver) issues.push({ path: `$.layers.${l.id}`, message: "stem_reactive layer must reference a stem driver" });
      if (input.availableAssetIds) {
        for (const ref of l.assetRefs) if (!input.availableAssetIds.includes(ref))
          issues.push({ path: `$.layers.${l.id}.assetRefs`, message: `asset ${ref} not available for export` });
      }
      for (const k of Object.keys(l.params)) if (/^(dmx|artnet|sacn|osc|midi|shader|channel)/i.test(k))
        issues.push({ path: `$.layers.${l.id}.params.${k}`, message: "renderer/hardware command params not allowed" });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "builder_scene_export");
  }
}

/** Starter, renderer-neutral visual scene templates. */
export const DJSEM_VISUAL_TEMPLATES: DJSEMVisualSceneTemplate[] = [
  {
    id: "tpl_ambient_intro", label: "Ambient Intro",
    baseScene: { label: "Ambient Intro", semanticUses: ["beginning"], chapterUses: ["intro"], cueUses: [], visualIntent: "ambient", intensity: 0.3, motion: "slow", density: 0.3, safetyLevel: "safe", rendererHintsAllowed: [] } as unknown as Omit<DJSEMVisualScene, "id">,
    defaultLayers: [
      { kind: "background", label: "Slow gradient", opacity: 1, params: { palette: "brand_primary" }, assetRefs: [] },
      { kind: "particles", label: "Dust", opacity: 0.4, params: { density: 0.2 }, assetRefs: [] },
    ],
  },
  {
    id: "tpl_peak_impact", label: "Peak Impact", requiredPlan: "creator_pro",
    baseScene: { label: "Peak Impact", semanticUses: ["triumph"], chapterUses: ["peak", "drop"], cueUses: ["drop"], visualIntent: "impact", intensity: 0.9, motion: "fast", density: 0.8, safetyLevel: "high_intensity", rendererHintsAllowed: [] } as unknown as Omit<DJSEMVisualScene, "id">,
    defaultLayers: [
      { kind: "background", label: "Strobe field", opacity: 1, params: {}, assetRefs: [] },
      { kind: "stem_reactive", label: "Kick pulse", opacity: 1, params: {}, stemDriver: "drums", assetRefs: [] },
      { kind: "typography", label: "Hook line", opacity: 0.9, params: {}, typographyMomentId: "tm_placeholder", assetRefs: [] },
    ],
  },
];
